import React from 'react';
import { styled, keyframes } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

const bounce = keyframes`
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-4px);
  }
`;

const IndicatorContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1, 2),
  gap: theme.spacing(1),
}));

const DotsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 3,
  backgroundColor: theme.palette.grey[200],
  padding: theme.spacing(0.75, 1.5),
  borderRadius: 12,
}));

const Dot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'delay',
})<{ delay: number }>(({ theme, delay }) => ({
  width: 6,
  height: 6,
  borderRadius: '50%',
  backgroundColor: theme.palette.text.secondary,
  animation: `${bounce} 1.4s ease-in-out infinite`,
  animationDelay: `${delay}ms`,
}));

const TypingText = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  fontStyle: 'italic',
}));

interface ChatTypingIndicatorProps {
  userNames: string[];
}

const ChatTypingIndicator: React.FC<ChatTypingIndicatorProps> = ({
  userNames,
}) => {
  if (userNames.length === 0) return null;

  const getText = () => {
    if (userNames.length === 1) {
      return `${userNames[0]} is typing`;
    }
    if (userNames.length === 2) {
      return `${userNames[0]} and ${userNames[1]} are typing`;
    }
    return `${userNames[0]} and ${userNames.length - 1} others are typing`;
  };

  return (
    <IndicatorContainer>
      <DotsContainer>
        <Dot delay={0} />
        <Dot delay={200} />
        <Dot delay={400} />
      </DotsContainer>
      <TypingText>{getText()}</TypingText>
    </IndicatorContainer>
  );
};

export default ChatTypingIndicator;
